import { storageConstants } from './constants/storageConstants';

/**
 * GA4/GTM 전송 전용 유틸입니다.
 *
 * 주의:
 * - 이 파일은 비즈니스 로직이 아닙니다.
 * - GTM 스크립트가 없거나 CSP/네트워크 이슈로 차단되어도 서비스 화면은 정상 동작해야 합니다.
 * - 개인정보 원문(이메일, 이름, 전화번호, 검색어/리뷰 본문 등)은 보내지 않습니다.
 * - 이벤트 값이 비어 있으면 GA4 분석 편의를 위해 "-"로 보냅니다.
 */
type AnalyticsPayload = {
  event: string;
  [key: string]: unknown;
};

type AnalyticsParams = Record<string, unknown>;

type LearnerErrorPayload = {
  error_type?: string;
  http_status?: number | string;
  error_code?: number | string;
  api_path?: string;
  api_method?: string;
  screen_name?: string;
  action_name?: string;
  handled?: boolean | string;
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const normalizeEnv = (value: unknown) => {
  const env = String(value ?? '').toLowerCase();

  if (env.includes('prod')) return 'prod';
  if (env.includes('stage')) return 'stage';
  if (env.includes('dev') || env.includes('local')) return 'dev';

  return env;
};

const isAnalyticsCollectionAllowed = () => {
  const { hostname } = window.location;
  const env = normalizeEnv(pickPhase(import.meta.env.VITE_APP_PHASE, import.meta.env.MODE));

  // 운영에서는 GTM 로드뿐 아니라 dataLayer 이벤트 적재 자체도 차단합니다.
  return (
    env !== 'prod' &&
    (hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.includes('dev') ||
      hostname.includes('stage'))
  );
};

const normalizeFalsyValues = (payload: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(payload).map(([key, value]) => [key, value || '-']));

const nullIfEmpty = (value: string | null) => (value === '' ? null : value);

const pickPhase = (phase: string | undefined, mode: string) => {
  const normalizedPhase = phase === '' ? undefined : phase;
  return normalizedPhase ?? mode;
};

const safeLocalStorageGet = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

// 모든 학습자 이벤트에 공통으로 붙는 GA4/GTM 파라미터입니다.
const getCommonParams = () => {
  const brandCode = safeLocalStorageGet(storageConstants.brandCode);
  const brandName = safeLocalStorageGet(storageConstants.brandName);
  const brandCompany = safeLocalStorageGet(storageConstants.brandCompany);
  const normalizedBrandCode = nullIfEmpty(brandCode);
  const normalizedBrandName = nullIfEmpty(brandName);
  const normalizedBrandCompany = nullIfEmpty(brandCompany);
  const phase = pickPhase(import.meta.env.VITE_APP_PHASE, import.meta.env.MODE);

  return {
    env: normalizeEnv(phase),
    app_name: 'learner',
    user_type: 'learner',
    tenant_id: normalizedBrandCompany ?? normalizedBrandCode,
    tenant_code: normalizedBrandCompany ?? normalizedBrandName ?? normalizedBrandCode,
    brand_code: normalizedBrandCode ?? normalizedBrandName,
    user_id: safeLocalStorageGet(storageConstants.memberId),
  };
};

/**
 * dataLayer에 이벤트를 적재합니다.
 * try/catch로 감싸 GA/GTM 수집 실패가 학습자 기능을 깨지 않도록 합니다.
 */
export const pushAnalyticsEvent = (event: string, params: AnalyticsParams = {}) => {
  try {
    if (!isAnalyticsCollectionAllowed()) {
      return;
    }

    window.dataLayer ??= [];

    const eventPayload = normalizeFalsyValues({
      ...params,
      ...getCommonParams(),
      event,
    });

    window.dataLayer.push(eventPayload);

    if (normalizeEnv(pickPhase(import.meta.env.VITE_APP_PHASE, import.meta.env.MODE)) !== 'prod') {
      console.debug('[GA4 dataLayer]', eventPayload);
    }
  } catch {
    // GA/GTM 수집 실패가 학습자 서비스 동작에 영향을 주면 안 되므로 무시합니다.
  }
};

// 학습자 클릭 이벤트에서 공통 분류값이 누락되면 GTM DLV가 undefined로 보입니다.
// 누락된 값도 GA4에서 필터링할 수 있도록 "-"로 치환될 기본 키를 항상 포함합니다.
const learnerClickBaseParams = {
  menu_1depth: undefined,
  menu_2depth: undefined,
  screen_type: undefined,
  click_action: undefined,
};

// 학습자 클릭 이벤트는 GTM Custom Event 이름을 learner_click으로 통일합니다.
export const pushLearnerClick = (params: AnalyticsParams = {}) => {
  pushAnalyticsEvent('learner_click', {
    ...learnerClickBaseParams,
    ...params,
  });
};

// 학습자 에러 이벤트는 비즈니스 로직과 분리하기 위해 이 함수 하나로만 dataLayer에 전송합니다.
// 개인정보 원문, 서버 응답 전문, stack trace, query string은 호출부에서 넘기지 않습니다.
export const pushLearnerError = (params: LearnerErrorPayload = {}) => {
  pushAnalyticsEvent('learner_error', params);
};

// SPA 라우터 이동 추적용 호환 함수입니다. 기존 virtual_page_view 호출부에서 사용합니다.
export const trackEvent = (payload: AnalyticsPayload) => {
  const { event, ...params } = payload;
  pushAnalyticsEvent(event, params);
};
